/**
 * Axios adapter over the platform's payload-crypto library.
 *
 * The library itself is not bundled: it is loaded by a script tag from the gateway origin
 * (/sts/js/payload-crypto.min.js) and exposes itself as window.PayloadCrypto. Sharing the
 * platform's client rather than reimplementing it means the wire format cannot drift from the
 * gateway that has to unseal it — the ECDH handshake, the JWE shape and the ticket header all
 * move together on their side.
 *
 * Everything below is plumbing between that library and axios. No crypto happens here.
 *
 * When the script is absent — it is not served on the standalone fs-dashboard-* origins — every
 * function here is inert and requests go out in plaintext, which is the behaviour those
 * deployments already have.
 */

const REINIT_HEADER = 'x-lsq-enc-reinit';

const lib = () => (typeof window === 'undefined' ? undefined : window.PayloadCrypto);

export const isPayloadCryptoAvailable = () => Boolean(lib());

const parseIfJson = (value) => {
  if (typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch (e) {
    // A sealed response that is not JSON is legitimate; hand back the plaintext untouched.
    return value;
  }
};

/**
 * axios splits a request across url/params/data, but prepareRequest wants one url string and one
 * body. getUri with an empty baseURL applies the instance's own param serializer, so arrays and
 * null params serialize exactly as they would on an unencrypted request.
 */
const toRequestUrl = (instance, config) => {
  try {
    return instance.getUri({ ...config, baseURL: '' });
  } catch (e) {
    return config.url;
  }
};

/**
 * Unseal a sealed error body in place.
 *
 * The success interceptor unseals; the error path did not, and it is the only other place a
 * sealed body reaches a call site. Call sites read error.response.data.message, which on a
 * sealed body is a property of a JWE string rather than an object, so it comes back undefined
 * and the UI falls back to a generic message, losing what the server actually said.
 *
 * decryptResponse returns its input unchanged when the body is not sealed, so this is inert on
 * plaintext errors. A failure here must never replace the original error: the caller needs the
 * status it actually got, not a decryption fault.
 */
const unsealErrorBody = async (payloadCrypto, error) => {
  const response = error?.response;
  if (!payloadCrypto || !response || response.data === undefined || response.data === null) {
    return;
  }

  try {
    const decrypted = await payloadCrypto.decryptResponse(response.data);
    if (decrypted !== response.data) {
      response.data = parseIfJson(decrypted);
    }
  } catch (e) {
    /* leave the body exactly as it arrived */
  }
};

export function attachPayloadCrypto(instance, { gatewayBaseUrl = '' } = {}) {
  const payloadCrypto = lib();
  if (!payloadCrypto) {
    return instance;
  }

  payloadCrypto.configure({ gatewayBaseUrl });

  instance.interceptors.request.use(async (config) => {
    // Stashed before anything is sealed so a 409 retry re-seals from the original rather than
    // sealing the ciphertext a second time.
    if (!config.lsqCryptoOriginal) {
      config.lsqCryptoOriginal = { url: config.url, params: config.params, data: config.data };
    }

    // prepareRequest resolves to { url, body, encrypted, headers } — an object, not a tuple.
    const prepared = await payloadCrypto.prepareRequest(toRequestUrl(instance, config), {
      body: config.data,
      gatewayBaseUrl
    });

    config.url = prepared.url;
    // The params are now inside `url`; leaving them set would append them a second time.
    config.params = undefined;
    if (prepared.body !== null && prepared.body !== undefined) {
      config.data = prepared.body;
    }
    // Its headers carry the ticket and, once a body has been sealed, Content-Type:
    // application/jose. The instance default of application/json would make the gateway
    // read a JWE as JSON, so these have to win over the instance headers.
    config.headers = { ...config.headers, ...prepared.headers };

    return config;
  });

  instance.interceptors.response.use(
    async (response) => {
      const decrypted = await payloadCrypto.decryptResponse(response.data);
      if (decrypted !== response.data) {
        response.data = parseIfJson(decrypted);
      }

      // The gateway asks for a fresh session out of band; renewing now keeps the cost off the
      // next request's critical path.
      if (response.headers?.[REINIT_HEADER]) {
        await payloadCrypto.reinit({ gatewayBaseUrl });
      }

      return response;
    },
    async (error) => {
      const config = error?.config;

      // Before any early return, and for every status: a sealed body is lost either way.
      await unsealErrorBody(payloadCrypto, error);
      // 409 is the gateway rejecting a ticket it can no longer unseal — an expired or rotated
      // session. One re-handshake and one replay, then give up so a persistent 409 cannot loop.
      if (error?.response?.status !== 409 || !config || config.lsqCryptoRetried) {
        return Promise.reject(error);
      }

      try {
        await payloadCrypto.reinit({ gatewayBaseUrl });
      } catch (e) {
        return Promise.reject(error);
      }

      return instance.request({
        ...config,
        ...(config.lsqCryptoOriginal || {}),
        lsqCryptoOriginal: undefined,
        lsqCryptoRetried: true
      });
    }
  );

  return instance;
}
